import { useCallback, useEffect, useRef, useState } from 'react';

import {
    LogStatus,
    LogsSyncProgress,
    OnboardDeviceLogsUploadStatus,
    PostOnboardDeviceLogsRequest,
} from '../interfaces';
import { postOnboardDeviceLogs } from '../api/postOnboardDeviceLogs';
import { getOnboardDeviceLogsUploadStatus } from '../api/getOnboardDeviceLogsUploadStatus';
import { useOnboardDeviceLogsSyncSocket } from './useOnboardDeviceLogsSyncSocket';

interface Params {
    deviceId: string;
    open: boolean;
    /** A log finished uploading, so the list of downloadable logs is out of date. */
    onLogsAvailable: () => void;
}

const countCompleted = (logs: LogStatus[]) =>
    logs.reduce((count, log) => (log.status === 'completed' ? count + 1 : count), 0);

/**
 * A worker that predates the two-pass plan reports no total, which would leave
 * the bar stuck on "scanning". Its per-log counts add up to the same number.
 */
const withKnownTotal = (progress: LogsSyncProgress, logs: LogStatus[]): LogsSyncProgress => {
    if (progress.expectedTotalFiles != null) {
        return progress;
    }

    const totalFiles = logs.reduce((sum, log) => sum + (log.totalFilesCount ?? 0), 0);

    return totalFiles > 0 ? { ...progress, expectedTotalFiles: totalFiles } : progress;
};

/**
 * Drives one device's logs sync.
 *
 * Live progress arrives over the websocket only - there is no polling. The API
 * is read exactly twice-over: once when the dialog opens on a sync that is
 * already running, and once whenever the socket (re)connects, which is the only
 * moment anything can have been missed.
 */
export const useOnboardDeviceLogsSync = ({ deviceId, open, onLogsAvailable }: Params) => {
    const STORAGE_KEY = `logs-upload-request-id-device-${deviceId}`;
    const [requestId, setRequestId] = useState<string | null>(localStorage.getItem(STORAGE_KEY));
    const [uploadStatus, setUploadStatus] = useState<string | null>(null);
    const [uploadDetails, setUploadDetails] = useState<string | null>(null);
    const [uploadInProgress, setUploadInProgress] = useState<boolean>(requestId !== null);
    const [progress, setProgress] = useState<LogsSyncProgress | null>(null);

    const onLogsAvailableRef = useRef(onLogsAvailable);
    onLogsAvailableRef.current = onLogsAvailable;

    const requestIdRef = useRef(requestId);
    requestIdRef.current = requestId;

    /** Counts applied snapshots, so a slow reload can tell a push overtook it. */
    const appliedSnapshotsRef = useRef(0);
    const reloadInFlightRef = useRef(false);
    /** The request we already wrapped up, so the last few pushes only land once. */
    const finishedRequestIdRef = useRef<string | null>(null);
    /** How many logs were downloadable last time we looked. */
    const completedLogsRef = useRef(0);

    /** One place to apply a picture of the sync, whoever brought it. */
    const applySnapshot = useCallback(
        (snapshot: OnboardDeviceLogsUploadStatus) => {
            appliedSnapshotsRef.current += 1;

            const logs = snapshot.logsStatuses ?? [];
            let logsBecameAvailable = false;

            if (snapshot.progress) {
                setProgress(withKnownTotal(snapshot.progress, logs));
            }

            if (snapshot.status) {
                setUploadStatus(snapshot.status);
            }

            if (snapshot.details) {
                setUploadDetails(snapshot.details);
            }

            const completedLogs = countCompleted(logs);

            if (completedLogs > completedLogsRef.current) {
                completedLogsRef.current = completedLogs;
                logsBecameAvailable = true;
            }

            const isFinished = Boolean(snapshot.status) && snapshot.status !== 'in progress';

            // Pushes keep arriving until the socket sees the cleared requestId, so
            // the wrap-up is guarded rather than run once per trailing snapshot.
            if (isFinished && finishedRequestIdRef.current !== snapshot.requestId) {
                finishedRequestIdRef.current = snapshot.requestId;
                setUploadInProgress(false);
                setRequestId(null);
                localStorage.removeItem(STORAGE_KEY);
                logsBecameAvailable = true;
            }

            if (logsBecameAvailable) {
                onLogsAvailableRef.current();
            }
        },
        [STORAGE_KEY],
    );

    const reloadSnapshot = useCallback(async () => {
        const currentRequestId = requestIdRef.current;

        if (!currentRequestId || reloadInFlightRef.current) {
            return;
        }

        reloadInFlightRef.current = true;
        const appliedBefore = appliedSnapshotsRef.current;

        try {
            const snapshot = await getOnboardDeviceLogsUploadStatus(currentRequestId);

            // A push that landed while this was in flight is the newer picture.
            if (appliedSnapshotsRef.current === appliedBefore) {
                applySnapshot(snapshot);
            }
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            reloadInFlightRef.current = false;
        }
    }, [applySnapshot]);

    useOnboardDeviceLogsSyncSocket({
        enabled: open && requestId !== null,
        requestId,
        onSnapshot: applySnapshot,
        onConnected: reloadSnapshot,
    });

    // The initial state, for a sync that was already running when the dialog
    // opened. Live updates take over from here.
    useEffect(() => {
        if (!open || !requestId) {
            return;
        }

        reloadSnapshot();
    }, [open, requestId, reloadSnapshot]);

    const startSync = async (payload: PostOnboardDeviceLogsRequest) => {
        setUploadStatus(null);
        setUploadDetails(null);
        setProgress(null);
        finishedRequestIdRef.current = null;
        completedLogsRef.current = 0;

        setUploadInProgress(true);

        try {
            const response = await postOnboardDeviceLogs(deviceId, payload);

            setRequestId(response.requestId);
            localStorage.setItem(STORAGE_KEY, response.requestId);
        } catch (error: any) {
            console.error('Upload error:', error);
            setUploadStatus('failed');
            setUploadDetails('Failed to start upload');
            setUploadInProgress(false);
        }
    };

    return {
        progress,
        uploadStatus,
        uploadDetails,
        uploadInProgress,
        startSync,
    };
};
